import { Input, Switch } from 'antd';
import { useMemo, type VFC } from 'react';
import { useCreateHardware, useUpdateHardware } from 'api/hooks';
import { HardwareCategorySelect } from 'components/form/select/HardwareCategorySelect';
import type { SerializedHardware } from '@enmon/commons/lib/tools';
import { useUserInfo } from '@/hooks/useUser';
import { Form, Modal, requiredTextRule } from 'components';
import type { HardwareFormValues } from '../../types/HardwareFormValues';
import type { HardwaresDataItem } from '../../queries/getHardwareDataList';
import { HARDWARE_FORM_ID } from '../../../../const';

interface HardwareModalProps {
  readonly hardware?: Partial<
    Pick<HardwaresDataItem, 'id' | 'disabled' | 'category' | 'external_id' | 'name' | 'enmon_details'>
  >;
  readonly onCancel: () => void;
  readonly onSuccess: (data: SerializedHardware) => void;
}

export const HardwareModal: VFC<HardwareModalProps> = ({ hardware, onCancel, onSuccess }) => {
  const { reseller } = useUserInfo();
  const { id } = hardware ?? {};
  const { mutate: createHardware, isLoading: isCreating } = useCreateHardware(onSuccess);
  const { mutate: updateHardware, isLoading: isUpdating } = useUpdateHardware(onSuccess);

  const initialValues = useMemo(
    () => ({
      ...(hardware ?? { disabled: false }),
      enmon_details: hardware?.enmon_details ? JSON.stringify(hardware.enmon_details, null, 2) : '',
    }),
    [hardware],
  );

  const handleOk = (values: HardwareFormValues) => {
    const parsed = {
      ...values,
      enmon_details: values.enmon_details ? JSON.parse(values.enmon_details as unknown as string) : null,
    };

    if (id) updateHardware({ id, values: parsed });
    else createHardware({ reseller, ...parsed });
  };

  return (
    <Modal
      centered
      open
      confirmLoading={isCreating || isUpdating}
      okButtonProps={{ htmlType: 'submit', form: HARDWARE_FORM_ID }}
      title={`${id ? 'Edit' : 'Create'} Hardware`}
      onCancel={onCancel}
    >
      <Form id={HARDWARE_FORM_ID} initialValues={initialValues} layout="vertical" name="hardware" onFinish={handleOk}>
        <Form.Item label="Name" name="name" rules={[requiredTextRule]}>
          <Input placeholder="Vodoměr ENBRA ER-AM DN15" />
        </Form.Item>
        <Form.Item label="External ID" name="external_id" rules={[requiredTextRule]}>
          <Input placeholder="123" />
        </Form.Item>
        <Form.Item label="Hardware Category" name="category" rules={[{ required: true }]}>
          <HardwareCategorySelect />
        </Form.Item>
        <Form.Item label="Priority" name="priority">
          <Input placeholder="123" />
        </Form.Item>
        <Form.Item label="Disabled" name="disabled" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item
          label="Enmon Details"
          name="enmon_details"
          rules={[
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                try {
                  JSON.parse(value);
                  return Promise.resolve();
                } catch {
                  return Promise.reject(new Error('Invalid JSON'));
                }
              },
            },
          ]}
        >
          <Input.TextArea autoSize={{ minRows: 3, maxRows: 10 }} placeholder='{"key": "value"}' />
        </Form.Item>
      </Form>
    </Modal>
  );
};
